import { ColDef, ColGroupDef, GridReadyEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { takeUntil } from "rxjs/operators";
import { ActiveStatusEnum, Feature, FeatureChoice, ManufacturerProductTypeConfig, Product, ProductType } from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import {
  PhaseoutDrainListDTO,
  PhaseoutDrainRowDTO,
  PhaseoutDrainStateEnum,
  ProductExposureApiService,
} from "app/shared/product-exposure-api.service";

const EntityTypes = { Product, ProductType, FeatureChoice, Feature, ManufacturerProductTypeConfig };

const StateNames: Record<PhaseoutDrainStateEnum, string> = {
  [PhaseoutDrainStateEnum.Drained]: "Drained",
  [PhaseoutDrainStateEnum.Draining]: "Draining",
  [PhaseoutDrainStateEnum.CannotCheck]: "Cannot check Proximity",
};

/**
 * Gatekeeper L3, the drain list: every style, product, choice, feature and configuration on
 * Phaseout, and whether its open work is done. A ProductAdmin makes a drained row Inactive here.
 * The save goes through the server's Inactive gate, so a row that took new work since the list was
 * read is still judged.
 */
@Component({
  selector: "app-phaseout-drain",
  templateUrl: "./phaseout-drain.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PhaseoutDrainComponent extends DomainBaseComponent implements OnInit {
  gridOptions: GridOptionsExt;
  list: PhaseoutDrainListDTO;
  drainedCount = 0;
  /** Rows ticked for Confirm Inactive on Selected. The tick lives on the row (_isPicked). */
  pickedCount = 0;

  constructor(
    domainService: DomainService,
    private route: ActivatedRoute,
    private productExposureApi: ProductExposureApiService,
  ) {
    super(domainService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => this.updateFromContext());
  }

  async updateFromContext() {
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady.bind(this),
      getRowId: (p) => `${p.data.EntityTypeName}-${p.data.EntityId}`,
      cellSelection: false,
      onCellValueChanged: () => this.countPicked(),
    });
    await this.wrapBusy(() => this.load());
    this.isPageReady = true;
  }

  private async load() {
    this.list = await this.productExposureApi.getPhaseoutDrain();
    this.drainedCount = this.list.Rows.filter((r) => r.State === PhaseoutDrainStateEnum.Drained).length;
  }

  async refresh() {
    await this.wrapBusy(() => this.load());
    AgFns.refreshGrid(this.gridOptions, this.list.Rows);
    this.countPicked();
  }

  /** A row a ProductAdmin can make Inactive now: drained. */
  canConfirm(r: PhaseoutDrainRowDTO) {
    return r?.State === PhaseoutDrainStateEnum.Drained && this.user.isProductAdmin;
  }

  private pickedRows(): PhaseoutDrainRowDTO[] {
    return (this.list?.Rows ?? []).filter((r) => (r as any)._isPicked && this.canConfirm(r));
  }

  private countPicked() {
    this.pickedCount = this.pickedRows().length;
  }

  onGridReady(event: GridReadyEvent) {
    const count = (headerName: string, field: keyof PhaseoutDrainRowDTO) =>
      ({ headerName, field, filter: false, type: "numericColumn", ...AgFns.fixWidth(140) }) as ColDef;
    const colDefs: (ColDef | ColGroupDef)[] = [
      {
        ...AgFns.createCheckboxProps("", "_isPicked", "pick"),
        // Only a row that shows Confirm Inactive gets a checkbox; the others show nothing.
        valueGetter: (p: any) => (this.canConfirm(p.data) ? !!p.data._isPicked : ""),
        valueSetter: (p: any) => {
          if (!this.canConfirm(p.data)) return false;
          p.data._isPicked = !!p.newValue;
          return true;
        },
        cellRendererSelector: (p: any) => (this.canConfirm(p.data) ? { component: "agCheckboxCellRenderer" } : undefined),
        editable: (p: any) => this.canConfirm(p.data),
        sortable: false,
        filter: false,
        ...AgFns.fixWidth(50),
      },
      {
        ...AgFns.createButtonProps("", this.confirmInactive.bind(this), {
          label: "Confirm Inactive",
          canDisplay: (r: PhaseoutDrainRowDTO) => this.canConfirm(r),
        }),
        ...AgFns.fixWidth(150),
      },
      {
        ...AgFns.createButtonProps("", this.openStyle.bind(this), {
          label: "Open",
          canDisplay: (r: PhaseoutDrainRowDTO) => r.ProductTypeId != null,
        }),
        ...AgFns.fixWidth(80),
      },
      { headerName: "Kind", field: "Kind", filter: "agSetColumnFilter", ...AgFns.fixWidth(130) },
      { headerName: "Manufacturer", field: "ManufacturerName", filter: "agSetColumnFilter", ...AgFns.flexWidth(150) },
      { headerName: "SKU", field: "Style", filter: "agTextColumnFilter", ...AgFns.fixWidth(130) },
      { headerName: "Name", field: "Name", filter: "agTextColumnFilter", ...AgFns.flexWidth(200, 2) },
      {
        headerName: "Drain",
        colId: "state",
        valueGetter: (p) => StateNames[(p.data as PhaseoutDrainRowDTO)?.State],
        filter: "agSetColumnFilter",
        ...AgFns.fixWidth(190),
      },
      count("Products", "ProductCount"),
      {
        headerName: "Open Work",
        children: [
          count("Job Order Lines", "OwedLines"),
          count("Purchase Order Lines", "OpenPoLines"),
          count("Units in Process", "ItemsInFlight"),
          count("Proximity Lines", "UnplacedLines"),
        ],
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "state", sort: "asc" }]);
  }

  openStyle(row: PhaseoutDrainRowDTO) {
    this.router.navigate(["/references/manage/product-type", row.ProductTypeId]);
  }

  async confirmInactive(row: PhaseoutDrainRowDTO) {
    const ok = await this.dialogService.yesNoDialog(
      "Make Inactive",
      `${row.Label} has no open work in Uniformax or Proximity.`
        + "<br/><br/>If you make it Inactive, no one can order it."
        + "<br/><br/>Do you want to make it Inactive?",
      "Make Inactive",
      "Cancel",
    );
    if (!ok) return;

    const saved = await this.wrapBusy(() => this.makeInactive(row));
    if (saved) this.toastr.success(`${row.Label} is Inactive.`);
    await this.refresh();
  }

  /** Confirm Inactive on Selected: one question, then one save for every ticked row. */
  async confirmInactiveSelected() {
    const rows = this.pickedRows();
    if (rows.length === 0) return;
    const ok = await this.dialogService.yesNoDialog(
      "Make Inactive",
      `${rows.length === 1 ? "1 item has" : `${rows.length} items have`} no open work in Uniformax or Proximity.`
        + "<br/><br/>If you make them Inactive, no one can order them."
        + "<br/><br/>Do you want to make them Inactive?",
      "Make Inactive",
      "Cancel",
    );
    if (!ok) return;

    const saved = await this.wrapBusy(async () => {
      const found = await Promise.all(rows.map(async (row) => ({
        row,
        entity: (await this.uow.fetchEntityByKey(EntityTypes[row.EntityTypeName] as any, row.EntityId))?.entity as
          Product | ProductType | FeatureChoice | Feature | ManufacturerProductTypeConfig,
      })));
      const ready = found.filter((x) => x.entity?.activeStatusId === ActiveStatusEnum.Phaseout);
      ready.forEach((x) => (x.entity.activeStatusId = ActiveStatusEnum.Inactive));
      if (ready.length > 0) await this.dbSaveService.saveOwnChanges(ready.map((x) => x.entity));
      return { done: ready.length, skipped: found.length - ready.length };
    });
    if (saved.done > 0) this.toastr.success(`${saved.done === 1 ? "1 item is" : `${saved.done} items are`} Inactive.`);
    if (saved.skipped > 0) {
      await this.dialogService.showOkMessage("Make Inactive",
        `${saved.skipped === 1 ? "1 item is" : `${saved.skipped} items are`} not on Phaseout now, so the system did not change them. The list shows their status now.`);
    }
    await this.refresh();
  }

  private async makeInactive(row: PhaseoutDrainRowDTO) {
    const r = await this.uow.fetchEntityByKey(EntityTypes[row.EntityTypeName] as any, row.EntityId);
    const entity = r?.entity as Product | ProductType | FeatureChoice | Feature | ManufacturerProductTypeConfig;
    if (entity == null || entity.activeStatusId !== ActiveStatusEnum.Phaseout) {
      await this.dialogService.showOkMessage("Make Inactive", `${row.Label} is not on Phaseout now. The list shows its status now.`);
      return false;
    }
    entity.activeStatusId = ActiveStatusEnum.Inactive;
    await this.dbSaveService.saveOwnChanges([entity]);
    return true;
  }
}
